import { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { translations, Language, TranslationKey } from '@/translations';

type TranslationKeyOrString = TranslationKey | (string & {});

interface LanguageContextType {
    language: Language;
    setLanguage: (_lang: Language) => void;
    t: (_key: TranslationKeyOrString) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguage] = useState<Language>('no'); // Default to Norwegian

    const t = useMemo(() => (key: TranslationKeyOrString): string => {
        const translation = translations[language] as Record<string, string>;
        const fallback = translations['no'] as Record<string, string>;
        return translation[key as string] || fallback[key as string] || key as string;
    }, [language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useTranslation must be used within a LanguageProvider');
    }
    return context;
}

export function translateAuthError(error: string): string {
    const errMap: Record<string, string> = {
        'Invalid login credentials': 'Feil e-post eller passord.',
        'User already registered': 'Bruker er allerede registrert.',
        'Password should be at least 8 characters': 'Passordet må være minst 8 tegn.',
        'Email not confirmed': 'E-posten er ikke bekreftet. Sjekk innboksen din.',
        'User not found': 'Bruker ikke funnet.',
        'Auth session missing!': 'Sesjonen utløpt. Logg inn på nytt.',
        'Rate limit exceeded': 'For mange forsøk. Supabase tillater maks 3 magiske lenker per time. Vent 10–60 minutter, eller bruk passord i stedet.',
        'over_email_send_rate_limit': 'For mange e-poster sendt. Vent noen minutter og prøv igjen, eller logg inn med passord.',
        'Signups not allowed for this instance': 'Nyregistrering er ikke tillatt.',
        'Email link is invalid or has expired': 'Lenken er ugyldig eller utløpt. Be om en ny.',
        'Token has expired or is invalid': 'Lenken er ugyldig eller utløpt. Be om en ny.',
        'Auth session failed': 'Autentisering feilet.',
        'staff_only_error': 'Kun ansatte har tilgang her. Bruk innbygger-innlogging.'
    };

    // Check specific known errors
    if (errMap[error]) return errMap[error];

    // Fallback for some common patterns
    if (error.includes('already registered')) return 'E-posten er allerede i bruk.';
    if (error.includes('password')) return 'Passordet oppfyller ikke kravene.';
    if (error.includes('rate limit') || error.includes('Rate limit')) {
        return 'For mange forsøk. Magiske lenker er begrenset til 3 per time. Prøv igjen om 10–60 minutter, eller bruk passord i stedet.';
    }
    if (error.includes('email') && error.includes('rate')) {
        return 'For mange e-poster sendt. Vent noen minutter og prøv igjen.';
    }

    return 'En ukjent feil oppstod. Prøv igjen.';
}
